import React, { FunctionComponent } from 'react';

import axios from 'axios';
import { forEachRight, get, isUndefined } from 'lodash';

import { hashtagSearchUrl, SourceId } from 'admin/sources/Sources';
import { AvatarType } from 'common/avatar/Avatar';
import {
    AssignCommentArgs,
    AssigneeType,
    Comment,
    CommentType,
    Conversation,
    GetAncestorsArgs,
    GetCommentsArgs,
    RemoveCommentsArgs,
    PayloadMessage,
    PollCommentsArgs,
    Lock,
    MediaType,
    MentionsData,
    Mention,
    CommentTypeGroup,
    UserType,
    CommentProperty,
    DataType,
    SaveConversationProps,
    CanPublishArgs,
    ConversationStatsResponse,
    ConversationStatType,
    BatchAggregateArgs,
    ForwardEmailRequest,
    SearchCommentHistoriesRequest,
    AttributionRequest,
    CommentFamily,
    BulkApprovalStatusUpdateRequest,
    ApprovalStatusUpdate,
} from 'common/comments2/Comments2.types';
import { Message } from 'common/comments2/message/Message';
import { Job } from 'common/jobs/Jobs';
import { ResponseChannel } from 'common/response-editor/menu-bar/MenuBar.types';
import { MediaType as MediaFormat } from 'common/upload/Upload';
import { BatchAggregateRequest } from 'core/util/Aggregate';
import { toMediaUrl } from 'core/util/Media';
import { Response } from 'core/util/Response';
import { SearchRequest } from 'core/util/Search';

interface Props {
    comment2: Comment;
}

const addSantizeTextArg = (args?: Record<string, any>) => {
    return { ...args, sanitizeText: !isUndefined(args?.sanitizeText) ? args?.sanitizeText : true };
};

export const getComments = async (
    tenantId: number | undefined,
    search: SearchRequest,
    args: GetCommentsArgs,
): Promise<Comment[]> => {
    const withSanitizeTextArg = addSantizeTextArg(args);

    const { data } = await axios.get<Comment[]>(`/api/tenants/${tenantId}/comments2`, {
        params: { search, args: withSanitizeTextArg },
    });

    return data;
};

export const saveComment = async (tenantId: number | undefined, comment: Comment): Promise<Comment> => {
    const { data } = await axios.post<Comment>(`/api/tenants/${tenantId}/comments2`, { comment });

    return data;
};

export const searchComments = async (
    tenantId: number | undefined,
    search: SearchRequest,
    args?: GetCommentsArgs,
): Promise<Comment[]> => {
    const withSanitizeTextArg = addSantizeTextArg(args);

    const { data } = await axios.post<Comment[]>(`/api/tenants/${tenantId}/comments2/search`, {
        search,
        args: withSanitizeTextArg,
    });

    return data;
};

export const searchCommentsElastic = async (
    tenantId: number | undefined,
    search: SearchRequest,
    args?: GetCommentsArgs,
): Promise<Comment[]> => {
    const withSanitizeTextArg = addSantizeTextArg(args);

    const { data } = await axios.post<Comment[]>(`/api/tenants/${tenantId}/comments2/search-elastic`, {
        search,
        args: withSanitizeTextArg,
    });

    return data;
};

export const getComment = async (
    tenantId: number | undefined,
    commentId: string | undefined,
    args?: GetCommentsArgs,
): Promise<Comment> => {
    const withSanitizeTextArg = addSantizeTextArg(args);

    const { data } = await axios.get<Comment>(`/api/tenants/${tenantId}/comments2/${commentId}`, {
        params: { args: withSanitizeTextArg },
    });

    return data;
};

export const setCommentLabel = async (
    tenantId: number | undefined,
    commentId: string,
    labelId: number,
): Promise<Comment> => {
    const { data } = await axios.put<Comment>(`/api/tenants/${tenantId}/comments2/${commentId}/set-label`, {
        labelId,
    });

    return data;
};

export const unsetCommentLabel = async (
    tenantId: number | undefined,
    commentId: string,
    labelId: number,
): Promise<Comment> => {
    const { data } = await axios.put<Comment>(`/api/tenants/${tenantId}/comments2/${commentId}/unset-label`, {
        labelId,
    });

    return data;
};

export const toggleCommentLabel = async (
    tenantId: number | undefined,
    comment: Comment,
    labelId: number,
): Promise<Comment> => {
    if (comment.labels?.includes(labelId)) {
        return unsetCommentLabel(tenantId, comment.id, labelId);
    }

    return setCommentLabel(tenantId, comment.id, labelId);
};

export const setCommentTags = async (
    tenantId: number | undefined,
    commentId: string,
    tags: number[],
): Promise<Comment> => {
    const { data } = await axios.post<Comment>(`/api/tenants/${tenantId}/comments2/${commentId}/set-tags`, {
        tags,
    });

    return data;
};

export const retryComment = async (tenantId: number | undefined, commentId: string, args: any): Promise<Comment> => {
    const { data } = await axios.put<Comment>(`/api/tenants/${tenantId}/comments2/${commentId}/retry`, { args });

    return data;
};

export const pollComments = async (
    tenantId: number | undefined,
    search: SearchRequest,
    args: PollCommentsArgs,
): Promise<PayloadMessage[]> => {
    const withSanitizeTextArg = addSantizeTextArg(args);

    const { data } = await axios.put<PayloadMessage[]>(`/api/tenants/${tenantId}/comments2/poll`, {
        search,
        args: withSanitizeTextArg,
    });

    return data;
};

export const sendConversation = async (
    tenantId: number | undefined,
    conversation: Conversation,
    args?: any,
): Promise<Conversation> => {
    const { data } = await axios.put<Conversation>(`/api/tenants/${tenantId}/comments2/conversation`, {
        conversation,
        args,
    });

    return data;
};

export const saveConversation = async (
    tenantId: number | undefined,
    conversation: Conversation,
    args?: any,
): Promise<Conversation> => {
    const { data } = await axios.put<Conversation>(`/api/tenants/${tenantId}/comments2/save-conversation`, {
        conversation,
        args,
    });

    return data;
};

export const assignComment = async (
    tenantId: number | undefined,
    commentId: string,
    args: AssignCommentArgs,
): Promise<Comment> => {
    const { data } = await axios.put<Comment>(`/api/tenants/${tenantId}/comments2/${commentId}/assign`, {
        tenantId,
        commentId,
        args,
    });

    return data;
};

export const generateCommentId = (id?: string): string => {
    if (id) return id;

    /* eslint-disable no-bitwise */
    const timestamp = ((Date.now() / 1000) | 0).toString(16);

    return timestamp + 'xxxxxxxxxxxxxxxx'.replace(/[x]/g, () => ((Math.random() * 16) | 0).toString(16)).toLowerCase();
    /* eslint-enable no-bitwise */
};

export const setLocalCommentLabel = (comment: Comment, labelId: number): void => {
    let currentConversationLabels: number[] = comment.labels || [];

    currentConversationLabels = currentConversationLabels.filter((label) => label !== labelId * -1);

    if (!currentConversationLabels.includes(labelId)) {
        currentConversationLabels.push(labelId);
    }

    comment.labels = currentConversationLabels; // eslint-disable-line no-param-reassign
};

export const Comment2: FunctionComponent<Props> = ({ comment2 }) => {
    switch (comment2.commentType) {
        case CommentType.Message: {
            return <Message comment2={comment2} />;
        }
        default:
            throw new TypeError(`unrecognized comment type ${comment2.commentType}`);
    }
};

export const searchConversations = async (
    tenantId: number | undefined,
    search: SearchRequest,
    args?: GetCommentsArgs,
): Promise<Comment[]> => {
    const withSanitizeTextArg = addSantizeTextArg(args);

    const { data } = await axios.post<Comment[]>(`/api/tenants/${tenantId}/comments2/search-conversations`, {
        search,
        args: withSanitizeTextArg,
    });

    return data;
};

export const getAncestors = async (
    tenantId: number | undefined,
    commentId: string,
    args: GetAncestorsArgs,
): Promise<any[]> => {
    const withSanitizeTextArg = addSantizeTextArg(args);

    const { data } = await axios.post<any[]>(`/api/tenants/${tenantId}/comments2/${commentId}/ancestors`, {
        args: withSanitizeTextArg,
    });

    return data;
};

export const addReaction = async (tenantId: number | undefined, commentId: string, reaction: number): Promise<{}> => {
    const { data } = await axios.post<{}>(`/api/tenants/${tenantId}/comments2/${commentId}/reaction`, {
        reaction,
    });

    return data;
};

export const getAvatarTypeFromAssigneeType = (comment: Comment): AvatarType => {
    return get(
        {
            [AssigneeType.User]: AvatarType.User,
            [AssigneeType.Role]: AvatarType.Role,
        },
        comment.assignee!.assigneeType,
    );
};

export const getCommentsCount = async (
    tenantId: number | undefined,
    search: SearchRequest,
    args?: GetCommentsArgs,
): Promise<any> => {
    const { data } = await axios.post<{}>(`/api/tenants/${tenantId}/comments2/count`, {
        search,
        args,
    });

    return data;
};

export const getCommentsCountElastic = async (
    tenantId: number | undefined,
    search: SearchRequest,
    args?: GetCommentsArgs,
): Promise<any> => {
    const { data } = await axios.post<{}>(`/api/tenants/${tenantId}/comments2/count-elastic`, {
        search,
        args,
    });

    return data;
};

export const lockComment = async (tenantId: number | undefined, commentId: string): Promise<Lock> => {
    const { data } = await axios.put<Lock>(`/api/tenants/${tenantId}/comments2/${commentId}/lock`);

    return data;
};

export const unlockComment = async (tenantId: number | undefined, commentId: string): Promise<string> => {
    const { data } = await axios.put<string>(`/api/tenants/${tenantId}/comments2/${commentId}/unlock`);

    return data;
};

export const getCommentLogoPath = (comment: Comment): string => {
    return comment.source?.message.logo || comment.source?.largeIconPath;
};

export const setDueDate = async (
    tenantId: number | undefined,
    commentId: string,
    dueDate?: number,
): Promise<Comment> => {
    const { data } = await axios.post(`/api/tenants/${tenantId}/comments2/${commentId}/due-date`, {
        dueDate,
    });

    return data;
};

export const getMediaType = (type: MediaFormat) => {
    switch (type) {
        case MediaFormat.Image:
            return MediaType.Photo;

        case MediaFormat.Audio:
            return MediaType.Audio;

        case MediaFormat.Video:
            return MediaType.Video;

        default:
            return MediaType.Video;
    }
};

export const mentionsParser = (text: string, mentionsData: MentionsData): string => {
    let parsedText = text;

    if (mentionsData && mentionsData.mentions) {
        forEachRight(mentionsData.mentions, (data: Mention) => {
            if (data.permalink) {
                const hyperText = text.substring(data.offset, data.offset + data.length);

                parsedText = `${parsedText.substring(0, data.offset)}<a class="parsed-mention" href="${
                    data.permalink
                }" target="_blank">${hyperText}</a>${parsedText.substring(data.offset + data.length)}`;
            }
        });
    }

    return parsedText;
};

export const parseHashTags = (text: string, sourceId: SourceId) => {
    const url = hashtagSearchUrl[sourceId];

    let parsedText = text;

    if (url && parsedText) {
        parsedText = parsedText.replace(/(^|\s)#([a-z\d-]+)/gi, `$1<a href="${url}$2" target="_blank">#$2</a>`);
    }

    return parsedText;
};

export const getCommentGroupIcon = (commentType: CommentType, sourceID?: string): string => {
    let groupIcon = '';

    if (sourceID && (sourceID === SourceId.SMS || sourceID === SourceId.WhatsApp)) {
        return groupIcon;
    }

    if (CommentTypeGroup.Reviews.includes(commentType)) {
        groupIcon = 'icon-star';
    } else if (CommentTypeGroup.SocialPosts.includes(commentType)) {
        groupIcon = 'icon-comments';
    } else if (CommentTypeGroup.Messages.includes(commentType)) {
        groupIcon = 'icon-envelope';
    }

    return groupIcon;
};

export const toComment2 = (currentConversation: Comment | undefined, args: Record<string, any>): Comment => {
    const { config, currentUser, response, parentID, replyToID } = args;
    const comment = {
        id: generateCommentId(),
        agencyID: currentConversation!.agencyID,
        tenantID: currentConversation!.tenantID,
        conversationID: currentConversation!.conversationID || currentConversation!.id,
        sourceUid: currentConversation!.sourceUid,
        customerID: currentConversation!.customerID,
        entityID: currentConversation!.entityID,
        poster: {
            id: String(currentUser.id),
            userType: UserType.User,
            name: currentUser.name,
            initials: currentUser.initials,
            color: currentUser.color,
            photoUrl: currentUser.image,
        },
        text: response.text,
        date: Date.now(),
        replyToID,
        parentID,
    } as Comment;

    switch (currentConversation?.commentType) {
        case CommentType.Review:
        case CommentType.ReviewReply:
            comment.commentType = CommentType.ReviewReply;
            break;

        case CommentType.SurveyConversation:
            comment.commentType = CommentType.SurveyReply;
            break;

        case CommentType.SocialAds:
        case CommentType.SocialPost:
        case CommentType.SocialPostReply:
            comment.commentType = CommentType.SocialPostReply;
            break;

        case CommentType.Message:
            comment.commentType = CommentType.Message;
            break;

        case CommentType.Mention:
        case CommentType.MentionReply:
            comment.commentType = CommentType.MentionReply;
            break;

        case CommentType.Survey:
        case CommentType.SurveyReply:
            comment.commentType = CommentType.SurveyReply;
            break;

        default:
            throw new TypeError(`Invalid commentType ${currentConversation?.commentType}`);
    }

    if (response.media && config) {
        comment.medias = response.media.map((media: any) => {
            return {
                mediaType: media.isImage ? MediaType.Photo : MediaType.Video,
                mimeType: media.type,
                name: media.name,
                thumbnailUrl: toMediaUrl(config!.urls.media, media.url, { w: 250, h: 250 }),
                title: media.name,
                url: media.url,
            };
        });
    }

    if (response.options.responseChannel === ResponseChannel.InternalNote) {
        comment.properties = {
            [CommentProperty.PrivateNote]: {
                name: CommentProperty.PrivateNote,
                dataType: DataType.Boolean,
                booleanValue: true,
                booleanValues: [true],
            },
        };
    }

    return comment;
};

export const createConversation = async (params: SaveConversationProps): Promise<string> => {
    const { data } = await axios.put<string>(`/api/tenants/${params.tenantID}/comments2/createConversation`, params);

    return data;
};

export const removeComment = async (
    tenantId: number | undefined,
    commentId: string | undefined,
    args: RemoveCommentsArgs,
): Promise<Response> => {
    const { data } = await axios.delete<Response>(`/api/tenants/${tenantId}/comments2/${commentId}`, {
        params: { args },
    });

    return data;
};

export const canPublish = async (tenantId: number | undefined, args: CanPublishArgs): Promise<boolean> => {
    const { data } = await axios.post<boolean>(`/api/tenants/${tenantId}/comments2/publish`, {
        args,
    });

    return data;
};

export const getConversationStats = async (
    tenantId: number | undefined,
    commentId: string | undefined,
    conversationStatTypes: ConversationStatType[],
): Promise<ConversationStatsResponse> => {
    const { data } = await axios.get<ConversationStatsResponse>(
        `/api/tenants/${tenantId}/comments2/${commentId}/conversation-stats`,
        {
            params: {
                args: {
                    conversationStatTypes,
                },
            },
        },
    );

    return data;
};

export const setConversationMetadata = async (
    tenantId: number | undefined,
    commentId: string | undefined,
    conversationMetadata: any,
): Promise<Comment> => {
    const { data } = await axios.post<Comment>(
        `/api/tenants/${tenantId}/comments2/${commentId}/conversation-metadata`,
        { ...conversationMetadata },
    );

    return data;
};

export const batchAggregate = async (tenantId: number, requests: BatchAggregateRequest, args: BatchAggregateArgs) => {
    const { data } = await axios.post(`/api/tenants/${tenantId}/comments2/aggregate`, {
        requests,
        args,
    });

    if (data?.length) {
        return data.map((metric: Record<string, any>) => {
            try {
                return JSON.parse(metric.json);
            } catch {
                return [];
            }
        });
    }

    return [];
};

export const forwardEmail = async (
    tenantId: number | undefined,
    commentId: string | undefined,
    forwardEmailRequest: ForwardEmailRequest,
): Promise<string> => {
    const { data } = await axios.post<string>(`/api/tenants/${tenantId}/comments2/${commentId}/forward`, {
        ...forwardEmailRequest,
    });

    return data;
};

export const getCommentHistories = async (
    tenantId: number | undefined,
    commentId: string | undefined,
    searchCommentHistoriesRequest: SearchCommentHistoriesRequest,
    args: GetCommentsArgs,
): Promise<Comment[]> => {
    const withSanitizeTextArg = addSantizeTextArg(searchCommentHistoriesRequest);
    const { data } = await axios.get<{ comment: Comment }[]>(
        `/api/tenants/${tenantId}/comments2/${commentId}/history`,
        {
            params: { searchCommentHistoriesRequest: withSanitizeTextArg, args },
        },
    );
    const histories = data.map(({ comment }) => comment);

    return histories;
};

export const addAttribution = async (
    tenantId: number | undefined,
    commentId: string | undefined,
    addReq: AttributionRequest,
    args?: any,
): Promise<Comment> => {
    const { data } = await axios.post<Comment>(`/api/tenants/${tenantId}/comments2/${commentId}/attribution`, {
        addReq,
        args,
    });

    return data;
};

export const removeAttribution = async (
    tenantID: number | undefined,
    commentId: string | undefined,
    removeReq: AttributionRequest,
    args?: Record<string, any>,
): Promise<Comment> => {
    const { data } = await axios.delete<Comment>(`/api/tenants/${tenantID}/comments2/${commentId}/attribution`, {
        params: {
            removeReq,
            args,
        },
    });

    return data;
};

export const getBulkPendingApprovals = async (
    tenantId: number | undefined,
    search: SearchRequest,
    args?: GetCommentsArgs,
): Promise<CommentFamily[]> => {
    const { data } = await axios.get<CommentFamily[]>(
        `/api/tenants/${tenantId}/comments2/bulk-actions/pending-approvals`,
        {
            params: {
                search,
                args,
            },
        },
    );

    return data;
};

export const bulkApprovalStatusUpdate = async (
    tenantId: number | undefined,
    bulkApprovalStatusUpdateRequest: BulkApprovalStatusUpdateRequest,
    args?: Record<string, boolean>,
): Promise<ApprovalStatusUpdate[]> => {
    const { data } = await axios.post<ApprovalStatusUpdate[]>(
        `/api/tenants/${tenantId}/comments2/bulk-actions/pending-approvals`,
        {
            bulkApprovalStatusUpdateRequest,
            args,
        },
    );

    return data;
};

export const exportReviewsAsJob = async (
    tenantId: number | undefined,
    search: SearchRequest,
    args?: Record<string, any>,
): Promise<Job> => {
    const { data } = await axios.post<Job>(`/api/tenants/${tenantId}/comments2/export-as-job`, {
        search,
        args,
    });

    return data;
};
